import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Reveal, HoldingsWordmark, SectionTitle } from "@/components/primitives";
import { FOUNDER_QUOTE_LEAD, FOUNDER_QUOTE_EMPH } from "@/lib/copy";

const VENTURES = [
  { name: "MAC — My Assistant CEO", to: "https://mac.nirvalife.com", testid: "venture-mac", square: false, bg: "var(--jmh-navy)", external: true },
  { name: "Steward & Stone — Strategic & Operational Advisory", to: "/steward-and-stone", img: "/brand/steward-stone-official.png", testid: "venture-steward-stone", square: false, bg: "var(--jmh-navy)", external: false },
  { name: "Strategic Assets — Intelligence, Recovery, Acquisitions & Real Estate", to: "/strategic-assets", img: "/brand/strategic-assets-official.png", testid: "venture-strategic-assets", square: true, bg: "var(--jmh-navy)", external: false },
  { name: "Nirva Life — Nervous System Intelligence", to: "https://nirvalife.com", img: "/brand/nirva-official.png", testid: "venture-nirva-life", square: true, bg: "var(--jmh-black)", external: true },
  { name: "Jennae Michelle Foundation", to: "/foundation", img: "/brand/foundation-official.png", testid: "venture-foundation", square: false, bg: "#f3f0e7", external: false },
];

// Thin refined gold outline whose illumination is continuously tied to the card's
// distance from viewport center (quiet -> peak at center -> settle). No hover/pulse.
const VentureCard = ({ v }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);
  const borderColor = useTransform(glow, (g) => `rgba(180,139,63,${(0.07 + 0.83 * g).toFixed(3)})`);
  const boxShadow = useTransform(
    glow,
    (g) =>
      `0 0 ${(1 + 18 * g).toFixed(1)}px rgba(180,139,63,${(0.16 * g).toFixed(3)}), inset 0 0 ${(10 * g).toFixed(1)}px rgba(180,139,63,${(0.07 * g).toFixed(3)})`
  );
  const inner = (
    <>
      <motion.div
        ref={ref}
        style={{ borderColor, boxShadow, backgroundColor: v.bg }}
        className={`border p-5 md:p-6 w-full ${v.square ? "aspect-square" : "aspect-[16/9]"} flex items-center justify-center transition-transform duration-500 group-hover:-translate-y-[2px]`}
      >
        {v.img ? <img src={v.img} alt={v.name} className="max-w-full max-h-full object-contain" /> : <span className="font-display text-center text-xl text-[var(--gold-lite)]">{v.name}</span>}
      </motion.div>
      <div className="mt-4 text-center font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)] opacity-60 group-hover:opacity-100 transition-opacity">
        {v.external ? "Visit →" : "Explore →"}
      </div>
    </>
  );
  const cls = "venture-snap block group max-w-[300px] sm:max-w-sm mx-auto w-full";
  if (v.external)
    return (
      <a href={v.to} target="_blank" rel="noopener noreferrer" data-testid={v.testid} className={cls} data-glow>
        {inner}
      </a>
    );
  return (
    <Link to={v.to} data-testid={v.testid} className={cls} data-glow>
      {inner}
    </Link>
  );
};

export default function Portfolio() {
  return (
    <div className="glow-black">
      <Seo title="Our Portfolio" description="Affiliated Ventures of Jennae Michelle Holdings — Steward & Stone, Nirva Life, Strategic Assets, MAC, and the Jennae Michelle Foundation." />
      <h1 className="sr-only">Our Portfolio</h1>

      <section className="pt-36 md:pt-48 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="corner-frame py-10 md:py-14 px-6">
              <HoldingsWordmark widthClass="max-w-[460px] md:max-w-[550px]" />
              <p className="mt-10 font-serif2 italic text-xl md:text-3xl text-[var(--bone)]/90">
                {FOUNDER_QUOTE_LEAD}<span className="gold-text font-serif2">{FOUNDER_QUOTE_EMPH}</span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 max-w-2xl mx-auto text-left space-y-4 text-[var(--bone)]/85 leading-relaxed">
              <p>These organizations were not created to say the same thing in different industries. Each exists because a different problem required a different kind of answer.</p>
              <p><span className="text-[var(--bone)]">Nirva Life</span> examines how people understand themselves and intentionally choose what comes next.</p>
              <p><span className="text-[var(--bone)]">Steward &amp; Stone</span> examines how organizations function and what their decisions require from the people responsible for carrying them out.</p>
              <p><span className="text-[var(--bone)]">Strategic Assets</span> examines financial, tax, property, and asset-based circumstances to determine which lawful possibilities the evidence supports.</p>
              <p><span className="text-[var(--bone)]">MAC — My Assistant CEO</span> coordinates work across the ecosystem and returns results and decisions to the Founder.</p>
              <p>The <span className="text-[var(--bone)]">Jennae Michelle Foundation</span> will direct resources toward experiences and initiatives that help people recognize and develop their own capabilities.</p>
              <p><span className="text-[var(--bone)]">Jennae Michelle Holdings</span> provides the structure connecting them without erasing what makes each one distinct.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-40 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Terminology preserved per founder direction; flagged for review. */}
          <Reveal><SectionTitle testid="portfolio-title">Affiliated Ventures</SectionTitle></Reveal>

          <div className="mt-16 md:mt-24 grid grid-cols-1 gap-24 md:gap-36 max-w-md mx-auto">
            {VENTURES.map((v) => (
              <VentureCard key={v.name} v={v} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
